import React, { useMemo } from 'react';

import { Setlist } from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { useAuth } from '@/providers/AuthProvider';
import { useServices } from '@/providers/ServicesProvider';
import { useSetlists } from '@/providers/SetlistsProvider';
import { useTeamSongs } from '@/providers/TeamSongsProvider';
import { Capacitor } from '@capacitor/core';
import { format, set } from 'date-fns';

import { Chip } from '../shared/Chip';
import { RoundedButton } from '../shared/RoundedButton';
import { TeamSongThumbs } from '../TeamSongThumbs';
import { UserAvatar } from '../UserAvatar';
import styles from './SetlistSummaryCard.module.scss';
import { getSetlistStatus, SetlistStatusEnum } from './utils';

const isCapacitor = Capacitor.isNativePlatform();

type SetlistSummaryCalendarProps = {
	className?: string;
	isLoading?: boolean;
	setlist?: Pick<Setlist, 'id' | 'startAt'>;
	setlistId?: string;
	startAtISO?: string;
	size?: 'sm' | 'md' | 'lg';
	isDisabled?: boolean;
	onClick?: () => void;
};

type SetlistSummaryTimeProps = {
	title?: string;
	titleClassName?: string;
};

type SetlistSummaryCardProps = SetlistSummaryCalendarProps &
	SetlistSummaryTimeProps & {
		calendarClassName?: string;
		timeClassName?: string;
		hasHover?: boolean;
		negativeSpacing?: boolean;
		padding?: boolean;
	};

export const SetlistSummaryCard: React.FC<SetlistSummaryCardProps> = ({
	className,
	calendarClassName,
	timeClassName,
	title,
	setlistId,
	isLoading,
	onClick = () => {},
	hasHover = true,
	negativeSpacing = true,
	padding = true,
}) => {
	const { setlists } = useSetlists();
	const { services } = useServices();
	const { findTeamSongsById } = useTeamSongs();

	const setlist = useMemo(() => {
		return setlists.find((s) => s.id === setlistId);
	}, [setlists, setlistId]);

	const service = useMemo(() => {
		return services.find((s) => s.id === setlist?.serviceId);
	}, [services, setlist?.serviceId]);

	const teamSongs = useMemo(() => {
		return findTeamSongsById(setlist?.teamSongIds || []);
	}, [setlist?.teamSongIds, findTeamSongsById]);

	const overallScore = useMemo(() => {
		return (
			Math.round(
				((setlist?.feedback?.overallScore || 0) + Number.EPSILON) * 10,
			) / 10
		);
	}, [setlist?.feedback?.overallScore]);

	if (!setlist) {
		return null;
	}

	return (
		<div
			className={classNames([
				styles['root'],
				!isCapacitor && hasHover && styles['hover'],
				negativeSpacing && styles['negative-spacing'],
				padding && styles['padding'],
				className,
			])}
			onClick={onClick}
		>
			<SetlistSummaryCalendar
				{...{ setlist, size: 'lg', className: calendarClassName }}
			/>
			<div className={styles['middle']}>
				<div className={styles['title']}>
					{service?.name || 'Custom service'}
				</div>
				<TeamSongThumbs
					className={styles['songs']}
					teamSongIds={teamSongs.map((ts) => ts?.id)}
				/>
			</div>
			<SetlistStatus
				setlistId={setlistId}
				className={styles['chip']}
				ctaMode
			/>

			{!!setlist.feedback?.submissions.length ? (
				<OverallScore value={overallScore} />
			) : (
				<UserAvatar
					size="x-small"
					activeSize="small"
					userId={setlist.userId}
				/>
			)}
		</div>
	);
};

export const SetlistSummaryCalendar: React.FC<SetlistSummaryCalendarProps> = ({
	className,
	setlist,
	startAtISO,
	isLoading,
	isDisabled,
	size = 'sm',
}) => {
	const classes = classNames([
		styles['calendar'],
		styles[`size-${size}`],
		isDisabled && styles['disabled'],
		className,
	]);

	if (!setlist?.startAt && !startAtISO) {
		return null;
	}

	// For some reason we need to cast the type here
	// The condition above will catch if both are undefined, so at this
	// point, one of them has to be defined and it's a string
	const startAt = new Date((setlist?.startAt || startAtISO) as string);
	const isLoaded = !isLoading;
	const month = isLoaded ? format(startAt, 'MMM') : '-';
	const day = isLoaded ? format(startAt, 'd') : '-';

	return (
		<div className={classes}>
			<div className={styles['month']}>{month}</div>
			<div className={styles['day']}>{day}</div>
		</div>
	);
};

export const SetlistSummaryTime: React.FC<SetlistSummaryCardProps> = ({
	className,
	title,
	setlist,
	isLoading,
	titleClassName,
	size = 'sm',
}) => {
	const classes = classNames([
		styles['time-holder'],
		styles[`size-${size}`],
		className,
	]);

	const isLoaded = !isLoading && !!setlist && !!setlist.startAt;
	const startAt = isLoaded ? new Date(setlist.startAt) : new Date();
	const time = isLoaded ? format(startAt, 'h:mm a') : '-:--';

	return (
		<div className={classes}>
			{size !== 'sm' && (
				<div className={classNames([styles['title'], titleClassName])}>
					{title}
				</div>
			)}
			<div className={styles['time']}>{time}</div>
		</div>
	);
};

type SetlistServiceTitleProps = {
	serviceId?: string | null;
	className?: string;
};

export const SetlistServiceTitle: React.FC<SetlistServiceTitleProps> = ({
	serviceId,
	className,
}) => {
	const { services } = useServices();

	const service = useMemo(() => {
		return services.find((s) => s.id === serviceId);
	}, [services, serviceId]);

	return (
		<div className={classNames([className, styles['title']])}>
			{service?.name || 'Custom Service'}
		</div>
	);
};

type SetlistStatusProps = {
	setlistId?: string | null;
	className?: string;
	isEdit?: boolean;
	ctaMode?: boolean;
};

export const SetlistStatus: React.FC<SetlistStatusProps> = ({
	setlistId,
	isEdit,
	className,
	ctaMode = false,
}) => {
	const { setlists } = useSetlists();

	const setlist = useMemo(() => {
		return setlists.find((s) => s.id === setlistId);
	}, [setlists, setlistId]);

	const { status, colorSchemeClassName } = getSetlistStatus(setlist, isEdit);

	const { user } = useAuth();
	const hasSubmitted = !!setlist?.submissions?.find(
		(sub) => sub.userId === user?.id,
	);
	const chipLabel =
		ctaMode && status === SetlistStatusEnum.IN_REVIEW && !hasSubmitted
			? 'Give Feedback'
			: status;

	// IN_REVIEW is currently a client-side 24h stub (see getSetlistStatus in utils.ts).
	// Once the server ships `inReview: Boolean`, this condition is unchanged — only the
	// status source changes.
	if (ctaMode && status === SetlistStatusEnum.IN_REVIEW && !hasSubmitted) {
		return (
			<RoundedButton color="blue" className={className}>
				{chipLabel}
			</RoundedButton>
		);
	}

	return (
		<Chip
			className={classNames([className, colorSchemeClassName])}
			variant={
				status === SetlistStatusEnum.EDITING ? 'outline' : 'rounded'
			}
			color="color-scheme"
		>
			{chipLabel}
		</Chip>
	);
};

type OverallScoreProps = {
	className?: string;
	value: number;
};

const OverallScore: React.FC<OverallScoreProps> = ({ className, value }) => {
	return <div className={classNames([styles['overall-score']])}>{value}</div>;
};
